import { ChargingPointStatus, ChargingPointStatusBucket } from '@/types/ChargingPoint'
import { StationStatus } from '@/types/Station'
import theme from '@/theme'

const CHARGING_POINT_STATUS_TO_BUCKET: Record<ChargingPointStatus, ChargingPointStatusBucket> = {
  [ChargingPointStatus.AVAILABLE]: 'AVAILABLE',
  [ChargingPointStatus.CHARGING]: 'OCCUPIED',
  [ChargingPointStatus.PREPARING]: 'OCCUPIED',
  [ChargingPointStatus.SUSPENDED_EV]: 'OCCUPIED',
  [ChargingPointStatus.SUSPENDED_EVSE]: 'OCCUPIED',
  [ChargingPointStatus.FINISHING]: 'OCCUPIED',
  [ChargingPointStatus.RESERVED]: 'UNAVAILABLE',
  [ChargingPointStatus.MAINTENANCE]: 'MAINTENANCE',
  [ChargingPointStatus.FAULTED]: 'FAULTED',
  [ChargingPointStatus.UNAVAILABLE]: 'UNAVAILABLE',
}

const BUCKET_LABEL: Record<ChargingPointStatusBucket, string> = {
  AVAILABLE: 'Disponível',
  OCCUPIED: 'Ocupado',
  MAINTENANCE: 'Em manutenção',
  FAULTED: 'Com defeito',
  UNAVAILABLE: 'Indisponível',
}

const BUCKET_COLOR_VALUE: Record<ChargingPointStatusBucket, string> = {
  AVAILABLE: theme.palette.success.main,
  OCCUPIED: theme.palette.warning.main,
  MAINTENANCE: theme.palette.error.main,
  FAULTED: theme.palette.grey[700],
  UNAVAILABLE: theme.palette.grey[700],
}

export type ChargingPointStatusChipColor = 'success' | 'warning' | 'error' | 'default'

const BUCKET_CHIP_COLOR: Record<ChargingPointStatusBucket, ChargingPointStatusChipColor> = {
  AVAILABLE: 'success',
  OCCUPIED: 'warning',
  MAINTENANCE: 'error',
  FAULTED: 'default',
  UNAVAILABLE: 'default',
}

const STATION_PIN_ICON: Record<StationStatus, string> = {
  AVAILABLE: '/assets/svg/pins/pin-available.svg',
  OCCUPIED: '/assets/svg/pins/pin-occupied.svg',
  MAINTENANCE: '/assets/svg/pins/pin-maintenance.svg',
  UNAVAILABLE: '/assets/svg/pins/pin-unavailable.svg',
}

const CHARGING_POINT_STATUS_LABEL: Record<ChargingPointStatus, string> = {
  [ChargingPointStatus.AVAILABLE]: 'Disponível',
  [ChargingPointStatus.CHARGING]: 'Carregando',
  [ChargingPointStatus.UNAVAILABLE]: 'Indisponível',
  [ChargingPointStatus.PREPARING]: 'Conectado',
  [ChargingPointStatus.SUSPENDED_EV]: 'Suspenso pelo veículo',
  [ChargingPointStatus.SUSPENDED_EVSE]: 'Suspenso pela estação',
  [ChargingPointStatus.FINISHING]: 'Finalizando',
  [ChargingPointStatus.RESERVED]: 'Reservado',
  [ChargingPointStatus.FAULTED]: 'Com defeito',
  [ChargingPointStatus.MAINTENANCE]: 'Em manutenção',
}

const STARTABLE_STATUSES: ChargingPointStatus[] = [ChargingPointStatus.AVAILABLE, ChargingPointStatus.PREPARING]

export interface ChargingPointAction {
  label: string
  disabled: boolean
}

export const chargingPointStatusUtils = {
  getBucket(status: ChargingPointStatus): ChargingPointStatusBucket {
    return CHARGING_POINT_STATUS_TO_BUCKET[status] ?? 'UNAVAILABLE'
  },

  getLabel(bucket: ChargingPointStatusBucket): string {
    return BUCKET_LABEL[bucket] ?? BUCKET_LABEL.UNAVAILABLE
  },

  getColorValue(bucket: ChargingPointStatusBucket): string {
    return BUCKET_COLOR_VALUE[bucket] ?? BUCKET_COLOR_VALUE.UNAVAILABLE
  },

  getChipColor(bucket: ChargingPointStatusBucket): ChargingPointStatusChipColor {
    return BUCKET_CHIP_COLOR[bucket] ?? BUCKET_CHIP_COLOR.UNAVAILABLE
  },

  getChipColorForStatus(status: ChargingPointStatus): ChargingPointStatusChipColor {
    return chargingPointStatusUtils.getChipColor(chargingPointStatusUtils.getBucket(status))
  },

  getGranularLabel(status: ChargingPointStatus): string {
    return CHARGING_POINT_STATUS_LABEL[status] ?? CHARGING_POINT_STATUS_LABEL[ChargingPointStatus.UNAVAILABLE]
  },

  getStationPinIcon(status: StationStatus): string {
    return STATION_PIN_ICON[status] ?? STATION_PIN_ICON.UNAVAILABLE
  },

  getAction(status: ChargingPointStatus, hasOwnSession = false): ChargingPointAction {
    if (hasOwnSession) return { label: 'Acompanhar carga', disabled: false }

    if (STARTABLE_STATUSES.includes(status)) return { label: 'Iniciar carga', disabled: false }

    return { label: chargingPointStatusUtils.getLabel(chargingPointStatusUtils.getBucket(status)), disabled: true }
  },
}
